import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { useGetAlertChartData } from "hooks/alerts/useGetAlertChartData";
import isNil from "lodash/isNil";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useGetRule } from "hooks/rules/useGetRule";
import { SeverityTypeContext } from "contexts/SeverityTypeContext";
import { Chart } from "components/Chart";
import { AggregateDropdown } from "components/AlertsManagement/AlertInfo/AggregateDropdown";
import { getSeriesFromRuleLevels } from "components/AlertsManagement/Chart/getSeriesFromRuleLevels";
import { getRuleLevelPerSeverityType } from "components/AlertsManagement/Chart/getRuleLevelPerSeverityType";
import { getAggregateChartOptions } from "components/AlertsManagement/Chart/getAggregateChartOptions";
import { getRawChartOptions } from "components/AlertsManagement/Chart/getRawChartOptions";
import { getSeverityColour } from "core/utils/severityTypes";

export const AlertTimeSeriesChart = React.memo(function AlertTimeSeriesChart({
  tagId,
  machineId,
  severityTypeId,
  ruleId,
  tagName,
  tagUnit,
  ruleName
}) {
  const [chartOptions, setChartOptions] = useState({});
  const [granularity, setGranularity] = useState("day");

  const {
    loading: severityTypesLoading,
    error: severityTypesError,
    alertSeverityTypes: severityTypes
  } = useContext(SeverityTypeContext);

  const { loading, error, data, isRaw } = useGetAlertChartData(
    tagId,
    machineId,
    granularity !== "raw" ? granularity : null
  );

  const { loading: ruleLoading, error: ruleError, rule } = useGetRule(ruleId);

  const thresholds = useMemo(() => {
    if (
      !ruleLoading &&
      !ruleError &&
      !severityTypesLoading &&
      !severityTypesError &&
      rule &&
      data
    ) {
      const relevantRuleLevels = getRuleLevelPerSeverityType(
        rule.rule_levels,
        machineId,
        severityTypes
      );

      return getSeriesFromRuleLevels(
        relevantRuleLevels,
        severityTypes,
        data.map(x => x.date_time),
        tagUnit
      );
    } else {
      return [];
    }
  }, [
    ruleLoading,
    ruleError,
    severityTypesLoading,
    severityTypesError,
    rule,
    data,
    machineId,
    severityTypes,
    tagUnit
  ]);

  useEffect(() => {
    if (
      !loading &&
      !error &&
      !isNil(data) &&
      !severityTypesLoading &&
      !severityTypesError
    ) {
      let updatedOptions = {};
      const severityType = getSeverityType(severityTypes, severityTypeId);
      const alertColour = getSeverityColour(severityType.colour);
      const yAxisTitle =
        tagUnit && tagUnit !== "" ? `${tagName} (${tagUnit})` : tagName;

      if (isRaw) {
        updatedOptions = getRawChartOptions(
          ruleName,
          alertColour,
          data,
          thresholds,
          yAxisTitle
        );
      } else {
        updatedOptions = getAggregateChartOptions(
          alertColour,
          data,
          tagUnit,
          tagName,
          ruleName,
          thresholds,
          yAxisTitle
        );
      }

      setChartOptions(prev => ({
        ...prev,
        ...updatedOptions
      }));
    }
  }, [
    data,
    ruleName,
    thresholds,
    tagUnit,
    tagName,
    severityTypesLoading,
    severityTypesError,
    severityTypes,
    severityTypeId,
    granularity,
    loading,
    error,
    isRaw
  ]);

  return (
    <ProgressIndicatorContainer loading={loading} error={error}>
      <Chart
        chartOptions={chartOptions}
        chartDropdowns={
          <AggregateDropdown
            granularity={granularity}
            onChange={setGranularity}
          />
        }
      />
    </ProgressIndicatorContainer>
  );
});

AlertTimeSeriesChart.propTypes = {
  tagId: PropTypes.number,
  machineId: PropTypes.number,
  severityTypeId: PropTypes.number,
  ruleId: PropTypes.number,
  tagName: PropTypes.string,
  tagUnit: PropTypes.string,
  ruleName: PropTypes.string
};

const getSeverityType = (severityTypes, severityTypeId) => {
  return severityTypes?.find(x => x.id === severityTypeId);
};
